import Dexie, { Table } from 'dexie';

// 定义缓存数据类型
export interface HomeDataCache {
  id?: number;
  data: any;
  timestamp: number;
  expiry: number; // 过期时间（毫秒）
}

// 创建数据库
class HomeCacheDB extends Dexie {
  homeData!: Table<HomeDataCache>;

  constructor() {
    super('HomeCacheDB');
    this.version(1).stores({
      homeData: '++id, timestamp, expiry',
    });
  }
}

const db = new HomeCacheDB();

// 缓存有效期：5分钟
const CACHE_DURATION = 5 * 60 * 1000;

/**
 * 当前环境是否支持 IndexedDB/Dexie
 * 注：API 存在不代表一定可用（如 iOS 无痕模式打开数据库可能失败），
 * 通过实际 db.open() 探测，结果缓存避免重复打开
 */
let supported: boolean | null = null;
async function isDexieSupport(): Promise<boolean> {
  if (supported != null) {
    return supported;
  }

  if (!('indexedDB' in window) || window.indexedDB == null) {
    supported = false;
    return supported;
  }

  try {
    await db.open();
    supported = true;
  } catch (error) {
    logDbError('打开数据库失败', error);
    supported = false;
  }

  return supported;
}

/**
 * 预连接数据库：避免首次操作时才惰性打开，
 * 也便于在页面加载早期暴露环境问题（如无无痕模式打开失败）
 * 失败不影响后续逻辑（后续操作会自动重试打开）
 */
isDexieSupport().then((ok) => {
  if (ok) {
    console.warn('[homeCache] 预连接数据库成功');
  }
});

/**
 * 打印 Dexie 错误详情（含底层错误类型，帮助定位失败原因）
 */
function logDbError(label: string, error: unknown) {
  const err = error as { name?: string; message?: string; inner?: unknown };
  const inner = err?.inner as { name?: string; message?: string } | undefined;
  console.error(`[homeCache] ${label}:`, {
    name: err?.name,
    message: err?.message,
    innerName: inner?.name,
    innerMessage: inner?.message,
    error,
  });
}

/**
 * 保存首页数据到 IndexedDB
 */
export async function saveHomeDataToCache(data: any): Promise<void> {
  if (!(await isDexieSupport())) {
    return;
  }

  // logDbStatus('saveHomeDataToCache');

  try {
    const timestamp = Date.now();
    const cacheData: HomeDataCache = {
      data,
      timestamp,
      expiry: timestamp + CACHE_DURATION,
    };

    // 清空旧数据，只保留最新的
    await db.homeData.clear();
    await db.homeData.add(cacheData);
  } catch (error) {
    logDbError('写入缓存失败', error);
  }
}

/**
 * 从 IndexedDB 读取首页数据
 * @param signal 取消信号：已被取消（如已有新调用发起）时 reject AbortError，
 *               读取期间被取消则结果作废
 */
export async function readHomeDataFromCache(signal?: AbortSignal): Promise<any | null> {
  if (!(await isDexieSupport())) {
    return null;
  }

  // 调用方已取消，直接中止
  if (signal?.aborted) {
    return null;
    // throw new DOMException('readHomeDataFromCache aborted', 'AbortError');
  }

  try {
    const cached = await db.homeData.orderBy('timestamp').last();

    // 读取期间被取消，结果作废
    if (signal?.aborted) {
      return null;
    }

    if (!cached) {
      return null;
    }

    // 检查是否过期
    if (Date.now() > cached.expiry) {
      await db.homeData.clear();
      return null;
    }

    return cached.data;
  } catch (error) {
    // 主动取消的请求静默忽略，交给最新一次调用处理
    if (signal?.aborted || (error as Error)?.name === 'AbortError') {
      return null;
    }
    logDbError('读取缓存失败', error);
    return null;
  }
}

/**
 * 清空首页数据缓存
 */
export async function clearHomeDataCache(): Promise<void> {
  if (!(await isDexieSupport())) {
    return;
  }

  try {
    await db.homeData.clear();
  } catch (error) {
    logDbError('清空缓存失败', error);
  }
}
